import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router-dom";
import { shopDisplayName } from "@/features/shops/lib";
import type { Shop, ShopConfiguration } from "@/shared/types/shop";
import { ArrowRight, Check, ChevronDown, Plus, Store } from "@/shared/ui/icons";

export interface WorkspacePickerProps {
  shops: Shop[];
  /** Shop mai sincronizzati: non si selezionano, si aprono nel wizard (WizardShop li preseleziona). */
  pendingShops: Shop[];
  current: ShopConfiguration;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (shop: Shop) => void;
}

export function WorkspacePicker({
  shops,
  pendingShops,
  current,
  open,
  onOpenChange,
  onSelect,
}: WorkspacePickerProps) {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const currentName = current.url ? shopDisplayName(current as Shop) : t("shell.store.none");

  useEffect(() => {
    if (!open) return undefined;
    const selectedOption = menuRef.current?.querySelector<HTMLElement>(
      '[role="option"][aria-selected="true"]',
    );
    (selectedOption || menuRef.current?.querySelector<HTMLElement>('[role="option"]'))?.focus();
    const closeOnOutsideClick = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) onOpenChange(false);
    };
    const handleKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onOpenChange(false);
        buttonRef.current?.focus();
        return;
      }
      if (!["ArrowDown", "ArrowUp"].includes(event.key)) return;
      const options = [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="option"]') || [])];
      if (!options.length) return;
      event.preventDefault();
      const currentIndex = options.indexOf(document.activeElement as HTMLElement);
      const nextIndex =
        event.key === "ArrowDown"
          ? (currentIndex + 1) % options.length
          : currentIndex <= 0
            ? options.length - 1
            : currentIndex - 1;
      options[nextIndex]?.focus();
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", handleKeyboard);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", handleKeyboard);
    };
  }, [open, onOpenChange]);

  return (
    <div className="workspace-picker">
      <span className="workspace-label" id="workspace-label">
        {t("shell.store.label")}
      </span>
      <button
        ref={buttonRef}
        type="button"
        className="workspace-trigger"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="workspace-options"
        aria-labelledby="workspace-label workspace-value"
      >
        <span className="workspace-trigger-icon">
          <Store aria-hidden="true" />
        </span>
        <span className="workspace-trigger-copy">
          <strong id="workspace-value">{currentName}</strong>
          <small>
            {current.language_iso ? current.language_iso.toUpperCase() : t("shell.store.choose")}
            {current.syncing ? ` · ${t("shell.store.syncing")}` : ""}
          </small>
        </span>
        <ChevronDown className="workspace-chevron" aria-hidden="true" />
      </button>
      {open && (
        <div ref={menuRef} className="workspace-menu">
          <div className="workspace-scroll">
            <div
              id="workspace-options"
              className="workspace-options"
              role="listbox"
              aria-label={t("shell.store.select")}
            >
              {shops.length ? (
                shops.map((shop) => {
                  const selected = String(shop.id) === String(current.id);
                  return (
                    <button
                      key={shop.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`workspace-option ${selected ? "is-selected" : ""}`}
                      onClick={() => onSelect(shop)}
                    >
                      <span className="workspace-option-language">
                        {shop.language_iso?.toUpperCase() || "—"}
                      </span>
                      <span>
                        <strong>{shopDisplayName(shop) || t("shell.store.unnamed")}</strong>
                        <small>
                          {shop.syncing
                            ? t("shell.store.syncing")
                            : shop.is_active
                              ? t("shell.store.active")
                              : t("shell.store.inactive")}
                        </small>
                      </span>
                      {selected && <Check aria-hidden="true" />}
                    </button>
                  );
                })
              ) : (
                <div className="workspace-empty">
                  <Store aria-hidden="true" />
                  <span>{t("shell.store.empty")}</span>
                </div>
              )}
            </div>
            {pendingShops.length > 0 && (
              <div className="workspace-pending" role="group" aria-labelledby="workspace-pending-label">
                <span className="workspace-pending-label" id="workspace-pending-label">
                  {t("shell.store.pending")}
                </span>
                {pendingShops.map((shop) => (
                  <Link
                    key={shop.id}
                    to={`/wizard/shop?shop=${encodeURIComponent(shop.id)}`}
                    className="workspace-option"
                    onClick={() => onOpenChange(false)}
                  >
                    <span className="workspace-option-language">
                      {shop.language_iso?.toUpperCase() || "—"}
                    </span>
                    <span>
                      <strong>{shopDisplayName(shop) || t("shell.store.unnamed")}</strong>
                      <small>{t("shell.store.pendingAction")}</small>
                    </span>
                    <ArrowRight aria-hidden="true" />
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="workspace-menu-footer">
            <NavLink to="/wizard" onClick={() => onOpenChange(false)}>
              <Plus aria-hidden="true" />
              {t("shell.store.add")}
            </NavLink>
          </div>
        </div>
      )}
    </div>
  );
}
